import { useQuery } from "@tanstack/react-query";
import { listMyCommissions, type AgentCommission } from "../lib/agent";
import { cn } from "../lib/cn";
import { AdminPreviewBanner, useIsAgent } from "../components/AdminPreviewBanner";
import { HelpBox, InfoTip } from "../components/InfoTip";

const STATUS_LABEL: Record<AgentCommission["status"], string> = {
  reserved: "Reserviert",
  approved: "Freigegeben",
  paid: "Ausgezahlt",
  clawed_back: "Zurückgefordert",
  cancelled: "Storniert",
};

const STATUS_STYLE: Record<AgentCommission["status"], string> = {
  reserved: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-900",
  paid: "bg-sky-100 text-sky-900",
  clawed_back: "bg-rose-100 text-rose-900",
  cancelled: "bg-slate-200 text-slate-700",
};

function fmtEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function AgentCommissions() {
  const userIsAgent = useIsAgent();
  const { data, isLoading, error } = useQuery<AgentCommission[]>({
    queryKey: ["agent-commissions"],
    queryFn: () => listMyCommissions(),
    enabled: userIsAgent,
  });

  return (
    <main className="mx-auto w-full max-w-6xl gradient-radial-warm px-4 py-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">
          Meine Provisionen
        </h2>
        <p className="mt-2 text-sm text-ink-muted">
          Alle Provisions-Posten aus Bestellungen, die dir zugeordnet wurden.
        </p>
      </div>

      {!userIsAgent && <AdminPreviewBanner pageName="Provisions" />}

      {userIsAgent && (
        <div className="mb-4">
          <HelpBox kind="info" collapsible title="📑 Wie liest sich diese Tabelle?">
            Jede Zeile ist <strong>eine Provision aus einer Bestellung</strong>.
            Bei mehreren Tier-Levels (du verdienst an Sub-Agenten mit) gibt es
            mehrere Zeilen pro Order — eine pro Tier.
            <br />
            <br />
            <strong>Spalten:</strong>
            <br />• <em>Tier 1</em> = du hast direkt verkauft
            <br />• <em>Tier 2</em> = ein Sub-Agent hat verkauft
            <br />• <em>Tier 3</em> = ein Sub-Sub-Agent hat verkauft
            <br />• <em>Bestellbetrag</em> = was der Kunde gezahlt hat
            <br />• <em>Provision</em> = was bei dir landet
            <br />• <em>Status</em> = wo der Betrag steht (Reserviert /
            Freigegeben / Ausgezahlt)
          </HelpBox>
        </div>
      )}

      {isLoading && <div className="text-sm text-ink-muted">Lade…</div>}
      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {error instanceof Error ? error.message : "Fehler"}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line bg-white/40 p-12 text-center">
          <p className="text-sm text-ink-muted">
            Noch keine Provisionen. Sobald jemand über deinen Link bestellt, tauchen die Beträge hier auf.
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="sm:hidden space-y-3">
          {data.map((ev) => {
            const name = [ev.customer_first_name, ev.customer_last_name]
              .filter(Boolean)
              .join(" ");
            return (
              <div key={ev.id} className="rounded-2xl border border-line bg-white p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-xs">#{ev.wc_order_id}</div>
                    <div className="text-[11px] text-ink-muted">
                      {new Date(ev.order_date).toLocaleDateString("de-DE")} · Tier {ev.tier_level}
                    </div>
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium flex-shrink-0",
                      STATUS_STYLE[ev.status],
                    )}
                  >
                    {STATUS_LABEL[ev.status]}
                  </span>
                </div>
                {(name || ev.customer_company) && (
                  <div className="mt-1 text-xs">
                    {name && <span className="font-medium">{name}</span>}
                    {ev.customer_company && (
                      <span className="text-ink-muted">{name ? " · " : ""}{ev.customer_company}</span>
                    )}
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-line/70 flex items-center justify-between text-xs">
                  <span className="text-ink-muted">
                    Bestellung {fmtEur(ev.product_amount_cents)}
                  </span>
                  <strong className="font-mono text-emerald-700 text-sm">
                    {fmtEur(ev.commission_amount_cents)}
                  </strong>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data && data.length > 0 && (() => {
        return (
          <div className="hidden sm:block overflow-x-auto rounded-2xl border border-line bg-white">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-cream-soft/40 text-ink-muted">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Datum</th>
                  <th className="text-left font-medium px-3 py-2">Order</th>
                  <th className="text-left font-medium px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      Kunde
                      <InfoTip title="Wer hat gekauft?" maxWidth={230}>
                        Vor- und Nachname. Bei Firmenkunden zusätzlich der
                        Firmenname. Wenn wir keinen Namen haben (z.B. WC-User
                        ohne Adresse-Daten): „—".
                      </InfoTip>
                    </span>
                  </th>
                  <th className="text-center font-medium px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      Tier
                      <InfoTip title="Tier-Level" maxWidth={220}>
                        1 = du selbst hast verkauft · 2 = ein direkter Sub-Agent
                        von dir hat verkauft · 3 = sein Sub-Agent hat verkauft.
                      </InfoTip>
                    </span>
                  </th>
                  <th className="text-right font-medium px-3 py-2">Bestellbetrag</th>
                  <th className="text-right font-medium px-3 py-2">Provision</th>
                  <th className="text-left font-medium px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      Status
                      <InfoTip title="Provisions-Status" maxWidth={240}>
                        <em>Reserviert</em> = nach Bestellung (14-Tage-Frist).{" "}
                        <em>Freigegeben</em> = bestätigt, wartet auf
                        Auszahlung. <em>Ausgezahlt</em> = überwiesen.{" "}
                        <em>Storniert/Zurückgefordert</em> = Kunde hat
                        widerrufen.
                      </InfoTip>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((ev) => (
                  <tr key={ev.id} className="border-t border-line">
                    <td className="px-3 py-2 text-xs text-ink-muted">
                      {new Date(ev.order_date).toLocaleDateString("de-DE")}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">#{ev.wc_order_id}</td>
                    <td className="px-3 py-2 text-xs">
                      {(() => {
                        const name = [ev.customer_first_name, ev.customer_last_name]
                          .filter(Boolean)
                          .join(" ");
                        if (name) {
                          return (
                            <>
                              <div className="font-medium text-ink">{name}</div>
                              {ev.customer_company && (
                                <div className="text-[10px] text-ink-muted">
                                  {ev.customer_company}
                                </div>
                              )}
                            </>
                          );
                        }
                        if (ev.customer_company) {
                          return <span className="font-medium">{ev.customer_company}</span>;
                        }
                        return <span className="text-ink-muted">—</span>;
                      })()}
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-xs">{ev.tier_level}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtEur(ev.product_amount_cents)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">
                      {fmtEur(ev.commission_amount_cents)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
                          STATUS_STYLE[ev.status],
                        )}
                      >
                        {STATUS_LABEL[ev.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}
    </main>
  );
}
